
import React, { useState } from 'react';
import { X, Mail, Lock, User, Flower, Loader2 } from 'lucide-react';
import { Language, User as UserType } from '../types';
import { database } from '../services/databaseService';

interface UserAuthModalProps {
  onClose: () => void;
  onLoginSuccess: (userData: UserType) => void;
  language: Language;
}

type AuthStep = 'login' | 'register';

const UserAuthModal: React.FC<UserAuthModalProps> = ({ onClose, onLoginSuccess, language }) => {
  const [step, setStep] = useState<AuthStep>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = {
    pt: {
      login: 'Entrar na Conta',
      register: 'Criar Nova Identidade',
      email: 'E-mail',
      pass: 'Senha',
      name: 'Nome de Usuário',
      confirm: 'Confirmar Registro',
      noAccount: 'Não possui uma conta?',
      hasAccount: 'Já possui uma identidade?',
      errorLogin: 'E-mail ou senha incorretos.',
      errorFields: 'Preencha todos os campos sagrados.',
      wait: 'Aguarde...',
    },
    en: {
      login: 'Sign In',
      register: 'Create New Identity',
      email: 'Email',
      pass: 'Password',
      name: 'Username',
      confirm: 'Confirm Registration',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an identity?',
      errorLogin: 'Incorrect email or password.',
      errorFields: 'Fill in all the sacred fields.',
      wait: 'Wait...',
    },
    es: {
      login: 'Iniciar Sesión',
      register: 'Crear Nueva Cuenta',
      email: 'Correo Electrónico',
      pass: 'Contraseña',
      name: 'Nombre de Usuario',
      confirm: 'Confirmar Registro',
      noAccount: '¿No tienes una cuenta?',
      hasAccount: '¿Ya tienes una cuenta?',
      errorLogin: 'Correo o contraseña incorrectos.',
      errorFields: 'Rellena todos los campos requeridos.',
      wait: 'Espere...',
    },
    ru: {
      login: 'Войти в Аккаунт',
      register: 'Создать Аккаунт',
      email: 'Эл. почта',
      pass: 'Пароль',
      name: 'Имя Пользователя',
      confirm: 'Подтвердить Регистрацию',
      noAccount: 'Нет аккаунта?',
      hasAccount: 'Уже есть аккаунт?',
      errorLogin: 'Неверный email или пароль.',
      errorFields: 'Заполните все необходимые поля.',
      wait: 'Подождите...',
    }
  }[language] || {
    login: 'Sign In',
    register: 'Create New Identity',
    email: 'Email',
    pass: 'Password',
    name: 'Username',
    confirm: 'Confirm Registration',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an identity?',
    errorLogin: 'Incorrect email or password.',
    errorFields: 'Fill in all the sacred fields.',
    wait: 'Wait...',
  };

  const handleAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (step === 'login') {
        const result = await database.signIn(email, password);
        
        if (result.error) {
          setError(result.error.message === 'Invalid login credentials' ? t.errorLogin : result.error.message);
        } else if (result.data && (result.data as any).user) {
          const userData = (result.data as any).user;
          onLoginSuccess({
            name: userData.name || userData.email?.split('@')[0],
            email: userData.email!,
            avatarUrl: userData.avatarUrl || ''
          });
          onClose();
        }
      } else if (step === 'register') {
        if (!name || !email || !password) {
          setError(t.errorFields);
        } else {
          const res = await database.signUp(email, password, name);
          if (res.error) {
            setError(res.error.message);
          } else if (res.data && res.data.user) {
            // Success registration results in automatic login
            onLoginSuccess({
              name: res.data.user.name,
              email: res.data.user.email,
              avatarUrl: res.data.user.avatarUrl || ''
            });
            onClose();
          }
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-6 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-black/98" onClick={onClose} />
      
      <div className="relative w-full max-w-lg bg-black border border-white/5 shadow-2xl overflow-hidden">
        <div className="h-1 bg-zinc-900 w-full">
          <div 
            className="h-full bg-[#c084fc] transition-all duration-700 shadow-[0_0_15px_#c084fc]" 
            style={{ width: step === 'login' ? '50%' : '100%' }}
          />
        </div>

        <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors z-10">
          <X className="w-6 h-6" />
        </button>

        <div className="p-12">
          <div className="text-center mb-10">
            <div className="inline-flex p-4 bg-white mb-6 border-b-4 border-[#c084fc] shadow-[0_0_15px_rgba(192,132,252,0.3)]">
              <Flower className="w-8 h-8 text-[#c084fc]" />
            </div>
            <h2 className="text-3xl font-black text-white uppercase tracking-tighter">
              {step === 'login' ? t.login : t.register}
            </h2>
          </div>

          <div className="space-y-6">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/50 text-red-500 text-[10px] font-black uppercase text-center animate-shake">
                {error}
              </div>
            )}
            
            <form onSubmit={handleAction} className="space-y-6">
              {step === 'login' ? (
                <>
                  <div className="space-y-4">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-700" />
                      <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.email} className="w-full bg-zinc-950 border border-zinc-800 py-5 pl-12 pr-6 text-white outline-none focus:border-[#c084fc]" />
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-700" />
                      <input required type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t.pass} className="w-full bg-zinc-950 border border-zinc-800 py-5 pl-12 pr-6 text-white outline-none focus:border-[#c084fc]" />
                    </div>
                  </div>
                  <button disabled={loading} className="w-full bg-white text-black font-black py-5 uppercase tracking-widest text-xs hover:bg-[#c084fc] hover:text-white transition-all flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.login}
                  </button>
                  <button type="button" onClick={() => setStep('register')} className="w-full text-[10px] text-zinc-500 font-black uppercase tracking-widest hover:text-white">{t.noAccount}</button>
                </>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-700" />
                      <input required type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t.name} className="w-full bg-zinc-950 border border-zinc-800 py-5 pl-12 pr-6 text-white outline-none focus:border-[#c084fc]" />
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-700" />
                      <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.email} className="w-full bg-zinc-950 border border-zinc-800 py-5 pl-12 pr-6 text-white outline-none focus:border-[#c084fc]" />
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-700" />
                      <input required type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t.pass} className="w-full bg-zinc-950 border border-zinc-800 py-5 pl-12 pr-6 text-white outline-none focus:border-[#c084fc]" />
                    </div>
                  </div>
                  <button disabled={loading} className="w-full bg-[#c084fc] text-white font-black py-5 uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-all flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.confirm}
                  </button>
                  <button type="button" onClick={() => setStep('login')} className="w-full text-[10px] text-zinc-500 font-black uppercase tracking-widest hover:text-white">{t.hasAccount}</button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAuthModal;
